/**
 * StepAboutYou — steg 1 i oppsettet av en familie (FF-11, #179).
 *
 * Systemadminen har opprettet familien (navn, kode, hjemsted) og adminen
 * (navn, brukernavn); adminen har valgt passord via invitasjonen og logget
 * inn. Her fyller hen ut sitt eget: fødselsdato, farge, kallenavn og egne
 * allergier og kosthold — med samme bekreftelse som for alle andre medlemmer
 * (D-02, merge-blocking). Systemadminen rører aldri kostholdet (D3).
 *
 * «Lagre» POSTer /api/onboarding/me; «Neste» krever at profilen er lagret
 * (nå eller tidligere — `profileConfirmed` fra statusen).
 */
import { useId, useState } from 'react';
import type { ReactElement } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { ColorSlot, OnboardingStatus } from '@stell/shared';
import { Button } from '../../../components/primitives/Button';
import { SectionLabel } from '../../../components/primitives/SectionLabel';
import { osloToday } from '../../../lib/osloToday';
import { useSession } from '../../auth/useSession';
import { AllergyChipPicker, type AllergyChipPickerValue } from '../components/AllergyChipPicker';
import { ColourSlotPicker } from '../components/ColourSlotPicker';
import { DietaryChipPicker, type DietaryChipPickerValue } from '../components/DietaryChipPicker';
import { WizardFooter } from '../components/WizardFooter';
import { OnboardingApi, OnboardingApiError } from '../api';
import { useOnboardingState } from '../useOnboardingState';

export function StepAboutYou({ status }: { status: OnboardingStatus }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const profileConfirmed = useOnboardingState((s) => s.profileConfirmed);
  const setProfile = useOnboardingState((s) => s.setProfile);
  const setStep = useOnboardingState((s) => s.setStep);
  const name = session?.member.name ?? session?.member.short_name ?? '';

  const [birthDate, setBirthDate] = useState('');
  const [shortName, setShortName] = useState('');
  const [colorSlot, setColorSlot] = useState<ColorSlot | null>(null);
  const [allergies, setAllergies] = useState<AllergyChipPickerValue>({ chips: [], custom: [], confirmed: false });
  const [allergyPending, setAllergyPending] = useState('');
  const [dietary, setDietary] = useState<DietaryChipPickerValue>({ chips: [], custom: [] });
  const [dietaryPending, setDietaryPending] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ids = { birthdate: useId(), nickname: useId() };
  const today = osloToday();

  const valid =
    birthDate !== '' &&
    birthDate <= today &&
    colorSlot !== null &&
    allergyPending.trim() === '' &&
    dietaryPending.trim() === '' &&
    allergies.confirmed;

  async function save(): Promise<void> {
    if (!valid || !colorSlot || saving) return;
    setSaving(true);
    setError(null);
    try {
      await OnboardingApi.saveProfile({
        birthDate,
        colorSlot,
        shortName: shortName.trim() || null,
        allergies: { chips: allergies.chips, custom: allergies.custom, confirmed: true },
        dietary,
        notes: null,
      });
      setProfile({ confirmed: true, colorSlot });
      setSaved(true);
    } catch (e) {
      const code = e instanceof OnboardingApiError ? e.body.error : null;
      setError(t(code === 'color_slot_taken' ? 'admin.members.error.color_taken' : 'onboarding.load_error'));
    } finally {
      setSaving(false);
    }
  }

  function next(): void {
    if (!profileConfirmed) return;
    setStep(2);
    navigate('/onboarding/step-2');
  }

  return (
    <section data-testid="step-about-you" className="flex flex-1 flex-col gap-5">
      <header className="flex flex-col gap-2">
        <h1 className="m-0 text-heading-l font-extrabold tracking-tight text-ink">{t('onboarding.about.title')}</h1>
        <p className="m-0 text-body text-ink">{t('onboarding.about.family', { name: status.familyName })}</p>
        <p data-testid="about-family-code" className="m-0 text-sm text-ink-2">
          {t('onboarding.about.code', { code: status.familyCode })}
        </p>
        <p className="m-0 text-sm text-ink-2">{t('onboarding.about.body')}</p>
      </header>

      <form
        className="flex flex-col gap-5"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <div className="grid grid-cols-2 gap-3">
          <div>
            <SectionLabel as="label" htmlFor={ids.birthdate}>
              {t('onboarding.step3.birthdate')}
            </SectionLabel>
            <input
              id={ids.birthdate}
              data-testid="about-birthdate"
              className="field"
              type="date"
              max={today}
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
            />
          </div>
          <div>
            <SectionLabel as="label" htmlFor={ids.nickname}>
              {t('onboarding.step3.nickname')}
            </SectionLabel>
            <input
              id={ids.nickname}
              data-testid="about-short-name"
              className="field"
              type="text"
              maxLength={40}
              value={shortName}
              onChange={(e) => setShortName(e.target.value)}
            />
          </div>
        </div>

        <ColourSlotPicker value={colorSlot} takenSlots={[]} onChange={setColorSlot} />

        <AllergyChipPicker
          memberName={name}
          value={allergies}
          onChange={setAllergies}
          pendingText={allergyPending}
          onPendingTextChange={setAllergyPending}
        />
        <DietaryChipPicker
          value={dietary}
          onChange={setDietary}
          pendingText={dietaryPending}
          onPendingTextChange={setDietaryPending}
        />

        {error ? (
          <p role="alert" data-testid="about-error" className="m-0 text-sm text-bad">
            {error}
          </p>
        ) : null}
        {saved ? (
          <p role="status" data-testid="about-saved" className="m-0 text-sm font-bold text-good">
            {t('onboarding.about.saved')}
          </p>
        ) : null}
        <Button type="submit" variant="secondary" disabled={!valid || saving} data-testid="about-save">
          {t('onboarding.about.save')}
        </Button>
      </form>

      <WizardFooter
        hint={
          profileConfirmed ? null : (
            <p data-testid="about-hint" className="m-0 text-center text-sm text-ink-2">
              {t('onboarding.about.need_profile')}
            </p>
          )
        }
      >
        <Button data-testid="about-next" size={56} className="flex-[2]" disabled={!profileConfirmed} onClick={next}>
          {t('onboarding.next')}
        </Button>
      </WizardFooter>
    </section>
  );
}
